import React, { useEffect, useState } from "react";
import { api, resolveAsset } from "../../lib/api";
import ImageUploader from "../ImageUploader";
import { Plus, Trash2, X, Save, Loader2 } from "lucide-react";

const EMPTY = { name: "", category: "Beverages", price: 0, description: "", image: "", order: 99 };

export default function MenuEditor({ reload }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const r = await api.get("/menu-items");
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => { fetchItems(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const data = { ...editing, price: Number(editing.price) || 0, order: Number(editing.order) || 0 };
      if (editing.id) {
        await api.put(`/menu-items/${editing.id}`, data);
      } else {
        await api.post("/menu-items", data);
      }
      setEditing(null);
      await fetchItems();
      reload && reload();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this dish?")) return;
    await api.delete(`/menu-items/${id}`);
    await fetchItems();
    reload && reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-medium text-gray-800">Menu Items</h2>
          <p className="text-xs text-gray-500">{items.length} dishes</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          className="bg-[#a08862] hover:bg-[#8e7654] text-white text-sm px-4 py-2 rounded flex items-center gap-1"
        >
          <Plus size={14} /> New Item
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white border rounded-md overflow-hidden">
              <div className="aspect-video bg-gray-100">
                {it.image && (
                  <img src={resolveAsset(it.image)} alt={it.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-gray-800">{it.name}</div>
                    <div className="text-xs text-gray-500">{it.category} • ₹{it.price}</div>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2 line-clamp-2">{it.description}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(it)} className="text-xs px-2 py-1 border rounded hover:bg-gray-50">Edit</button>
                  <button onClick={() => remove(it.id)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
              <h3 className="font-medium">{editing.id ? "Edit Item" : "New Item"}</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name"><Inp value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} /></Field>
                <Field label="Category"><Inp value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} /></Field>
                <Field label="Price (₹)"><Inp type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} /></Field>
                <Field label="Order"><Inp type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} /></Field>
              </div>
              <Field label="Description">
                <textarea
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-[#a08862]"
                  rows={3}
                />
              </Field>
              <Field label="Image">
                <ImageUploader value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border rounded">Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="bg-[#a08862] hover:bg-[#8e7654] text-white text-sm px-4 py-2 rounded flex items-center gap-1"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
function Inp({ value, onChange, type = "text" }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-[#a08862]"
    />
  );
}
